import React, { useState } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'

/**
 * Reusable Pagination Controls Component
 * Displays page numbers with Next/Previous buttons
 * Responsive for desktop and mobile
 */
export default function PaginationControls({
  currentPage = 1,
  totalPages = 1,
  onPageChange = () => {},
  loading = false,
  className = '',
  // Optional section styling props
  website,
  sectionFormStyling,
  section,
  useDefaultStyling = true,
  sectionType = 'pagination-controls'
}) {
  const [isHovered, setIsHovered] = useState(false)
  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: isHovered ? (formColors?.hoverBackgroundColor || formColors?.backgroundColor || DEFAULT_FORM_COLORS.hoverBackgroundColor) : (formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor),
    color: isHovered ? (formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor) : (formColors?.textColor || DEFAULT_FORM_COLORS.textColor)
  } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  if (totalPages <= 1) return null

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
      // Scroll to top of content
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
      // Scroll to top of content
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePageClick = (pageNum) => {
    onPageChange(pageNum)
    // Scroll to top of content
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Calculate which page numbers to display
  const getPageNumbers = () => {
    const pages = []
    const maxPagesToShow = window.innerWidth < 640 ? 3 : 5 // Show fewer on mobile

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show current page and nearby pages
      let start = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2))
      let end = Math.min(totalPages, start + maxPagesToShow - 1)

      if (end - start + 1 < maxPagesToShow) {
        start = Math.max(1, end - maxPagesToShow + 1)
      }

      if (start > 1) pages.push(1)
      if (start > 2) pages.push('...')

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (end < totalPages - 1) pages.push('...')
      if (end < totalPages) pages.push(totalPages)
    }

    return pages
  }

  const pageNumbers = getPageNumbers()
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPages

  return (
    <div
      className={`flex items-center justify-center gap-1 sm:gap-2 my-6 sm:my-8 flex-wrap px-2 ${className}`}
      style={formStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Previous Button */}
      <button
        onClick={handlePrevious}
        disabled={isFirstPage || loading}
        className="flex items-center gap-1 px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
        title="Previous page"
        style={buttonStyle}
        onMouseEnter={(e) => {
          if (isButtonStyling) {
            e.currentTarget.style.backgroundColor = sectionFormStyling.hoverButtonBackgroundColor || sectionFormStyling.buttonBackgroundColor
            e.currentTarget.style.color = sectionFormStyling.hoverButtonTextColor || sectionFormStyling.buttonTextColor
          }
        }}
        onMouseLeave={(e) => {
          if (isButtonStyling) {
            e.currentTarget.style.backgroundColor = sectionFormStyling.buttonBackgroundColor
            e.currentTarget.style.color = sectionFormStyling.buttonTextColor
          }
        }}
      >
        <FiChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
        <span className="hidden sm:inline">Previous</span>
      </button>

      {/* Page Numbers */}
      <div className="flex items-center gap-1 md:gap-2 flex-wrap justify-center">
        {pageNumbers.map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span className="px-2 py-1 text-gray-400 text-sm md:text-base">...</span>
            ) : (
              <button
                onClick={() => handlePageClick(page)}
                disabled={loading}
                className={`w-7 sm:w-8 h-7 sm:h-8 md:w-10 md:h-10 rounded-lg text-xs sm:text-sm md:text-base font-medium transition-all ${
                  page === currentPage
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed'
                }`}
                title={`Go to page ${page}`}
                style={buttonStyle}
                onMouseEnter={(e) => {
                  if (isButtonStyling) {
                    e.currentTarget.style.backgroundColor = sectionFormStyling.hoverButtonBackgroundColor || sectionFormStyling.buttonBackgroundColor
                    e.currentTarget.style.color = sectionFormStyling.hoverButtonTextColor || sectionFormStyling.buttonTextColor
                  }
                }}
                onMouseLeave={(e) => {
                  if (isButtonStyling) {
                    e.currentTarget.style.backgroundColor = sectionFormStyling.buttonBackgroundColor
                    e.currentTarget.style.color = sectionFormStyling.buttonTextColor
                  }
                }}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Next Button */}
      <button
        onClick={handleNext}
        disabled={isLastPage || loading}
        className="flex items-center gap-1 px-3 py-2 md:px-4 md:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
        title="Next page"
        style={buttonStyle}
        onMouseEnter={(e) => {
          if (isButtonStyling) {
            e.currentTarget.style.backgroundColor = sectionFormStyling.hoverButtonBackgroundColor || sectionFormStyling.buttonBackgroundColor
            e.currentTarget.style.color = sectionFormStyling.hoverButtonTextColor || sectionFormStyling.buttonTextColor
          }
        }}
        onMouseLeave={(e) => {
          if (isButtonStyling) {
            e.currentTarget.style.backgroundColor = sectionFormStyling.buttonBackgroundColor
            e.currentTarget.style.color = sectionFormStyling.buttonTextColor
          }
        }}
      >
        <span className="hidden sm:inline">Next</span>
        <FiChevronRight className="w-4 h-4 md:w-5 md:h-5" />
      </button>

      {/* Page Info */}
      <div className="w-full text-center text-xs md:text-sm text-gray-500 mt-2">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  )
}
